import { createWelcomeStep, createMiddleStep, createFinalStep } from '../config/stepTemplates';
import { waitForElement, waitForReduxState, fillReactInput } from '../utils/tourUtils';
import {
  searchLegalNotices,
  setEntreprisesQuery,
  resetEntreprises,
} from '../../../redux/actions/entreprisesAction';

const TOTAL = 10;
const DEMO_QUERY = 'Modification du capital';

export function getAnnonceLegaleTourSteps(t, { navigate, dispatch, store }) {
  return [
    // 1 — Welcome (centered) — explain what legal notices are
    {
      ...createWelcomeStep({
        id: 'annonce-welcome',
        title: t('onboarding_tour.annonce.step1.title', 'Legal Notices & Company Intelligence'),
        text: t(
          'onboarding_tour.annonce.step1.text',
          "Access every official legal publication in Morocco — company constitutions, capital changes, dissolutions, merger announcements, share transfers, and more. Use it for due diligence, competitor monitoring, or tracking your own clients' activity."
        ),
        t,
        totalSteps: TOTAL,
      }),
      when: {
        show() {
          // Reset state for clean tour start
          dispatch(resetEntreprises());
        },
      },
    },

    // 2 — Search — perform real search for "Modification du capital"
    {
      id: 'annonce-search',
      title: t('onboarding_tour.annonce.step2.title', 'Intelligent Search'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((2 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 2, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.annonce.step2.text', 'Search by company name, registration number (RC), city, or by the exact type of announcement — like a capital change, dissolution, or management change. Let\'s try searching for "Modification du capital".')}</p>`,
      attachTo: { element: '[data-tour="entreprises-search"]', on: 'bottom' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.search_btn', 'Search →'),
          classes: 'shepherd-button-primary lawis-annonce-search-btn',
          action: function () {
            const btn = document.querySelector('.lawis-annonce-search-btn');
            if (btn) {
              btn.textContent = '⏳ ' + t('onboarding_tour.searching', 'Searching...');
              btn.style.pointerEvents = 'none';
              btn.style.opacity = '0.7';
            }

            // Fill input visually
            const input = document.querySelector('[data-tour="entreprises-search"] input');
            if (input) {
              fillReactInput('[data-tour="entreprises-search"] input', DEMO_QUERY);
            }

            // Dispatch search directly
            dispatch(setEntreprisesQuery(DEMO_QUERY));
            dispatch(searchLegalNotices({ q: DEMO_QUERY, page: 1, filters: {} }));

            const tour = this;
            waitForReduxState(
              store,
              (s) => !s.entreprises?.loading?.search && s.entreprises?.items?.length > 0,
              15000
            )
              .then(() => waitForElement('[data-tour="annonce-card"]', 5000))
              .then(() => setTimeout(() => tour.next(), 500))
              .catch(() => tour.next());
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="entreprises-search"]', 5000).catch(() => {});
      },
    },

    // 3 — Filters
    {
      ...createMiddleStep({
        id: 'annonce-filters',
        title: t('onboarding_tour.annonce.step3.title', 'Refine Your Results'),
        text: t(
          'onboarding_tour.annonce.step3.text',
          'Filter by announcement type (constitution, dissolution, capital change...), legal form (SARL, SA, SAS), city, business sector, company status, or publication year. Every filter shows how many results match.'
        ),
        attachTo: { element: '[data-tour="entreprises-filters"]', on: 'right' },
        t,
        stepIndex: 3,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="entreprises-filters"]', 5000).catch(() => {});
      },
    },

    // 4 — Results list
    {
      ...createMiddleStep({
        id: 'annonce-results',
        title: t('onboarding_tour.annonce.step4.title', 'Search Results'),
        text: t(
          'onboarding_tour.annonce.step4.text',
          'Each card shows the announcement type, the company name, legal form, capital, registration number (RC), city, and publication date. Click any card to read the full announcement.'
        ),
        attachTo: { element: '[data-tour="entreprises-results"]', on: 'left' },
        t,
        stepIndex: 4,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="entreprises-results"]', 5000).catch(() => {});
      },
    },

    // 5 — Focus on third result + navigate into it
    {
      id: 'annonce-open-third',
      title: t('onboarding_tour.annonce.step5.title', 'Open an Announcement'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((5 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 5, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.annonce.step5.text', 'Let\'s open this announcement to see everything the official publication contains — parties, amounts, dates, and the raw text.')}</p>`,
      attachTo: { element: '[data-tour="annonce-target-card"]', on: 'left' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.next', 'Next'),
          classes: 'shepherd-button-primary',
          action: function () {
            // Click the third card
            const target = document.querySelector('[data-tour="annonce-target-card"]');
            if (target) target.click();

            const tour = this;
            waitForElement('[data-tour="annonce-detail-header"]', 5000)
              .then(() => setTimeout(() => tour.next(), 500))
              .catch(() => tour.next());
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="annonce-card"]', 5000)
          .then(() => {
            // Find the third card and tag it
            const cards = document.querySelectorAll('[data-tour="annonce-card"]');
            const target = cards.length >= 3 ? cards[2] : cards[0];
            if (target) {
              target.setAttribute('data-tour', 'annonce-target-card');
              target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return new Promise((r) => setTimeout(r, 400));
          })
          .catch(() => {});
      },
    },

    // 6 — Announcement detail header (type, company name, date, RC)
    {
      ...createMiddleStep({
        id: 'annonce-detail-header',
        title: t('onboarding_tour.annonce.step6.title', 'Full Announcement'),
        text: t(
          'onboarding_tour.annonce.step6.text',
          'The header shows the announcement type, the company\'s legal form, registration number, publication date, and the official Bulletin Officiel reference. This is the authoritative legal record.'
        ),
        attachTo: { element: '[data-tour="annonce-detail-header"]', on: 'bottom' },
        t,
        stepIndex: 6,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="annonce-detail-header"]', 5000).catch(() => {});
      },
    },

    // 7 — Key figures (capital, dates, shares)
    {
      ...createMiddleStep({
        id: 'annonce-detail-figures',
        title: t('onboarding_tour.annonce.step7.title', 'Key Figures'),
        text: t(
          'onboarding_tour.annonce.step7.text',
          'Capital, creation date, registration date, duration, and share structure — everything extracted and structured from the original publication for quick analysis.'
        ),
        attachTo: { element: '[data-tour="annonce-detail-figures"]', on: 'top' },
        t,
        stepIndex: 7,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="annonce-detail-figures"]', 5000)
          .then((el) => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return new Promise((r) => setTimeout(r, 400));
          })
          .catch(() => {});
      },
    },

    // 8 — View company → navigate to company profile
    {
      id: 'annonce-view-company',
      title: t('onboarding_tour.annonce.step8.title', 'See the Full Company Profile'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((8 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 8, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.annonce.step8.text', 'Click here to see the consolidated company profile — all its announcements, shareholders, activity, legal identifiers, and complete history in one place.')}</p>`,
      attachTo: { element: '[data-tour="annonce-view-company"]', on: 'bottom' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.next', 'Next'),
          classes: 'shepherd-button-primary',
          action: function () {
            const link = document.querySelector('[data-tour="annonce-view-company"]');
            if (link) link.click();

            const tour = this;
            waitForElement('[data-tour="company-header"]', 5000)
              .then(() => setTimeout(() => tour.next(), 500))
              .catch(() => tour.next());
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="annonce-view-company"]', 5000)
          .then((el) => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return new Promise((r) => setTimeout(r, 400));
          })
          .catch(() => {});
      },
    },

    // 9 — Company header + shareholders
    {
      ...createMiddleStep({
        id: 'annonce-company-header',
        title: t('onboarding_tour.annonce.step9.title', 'Company Profile'),
        text: t(
          'onboarding_tour.annonce.step9.text',
          'The profile consolidates the company\'s identity — name, legal form, capital, RC, ICE, address, business sector, and key dates. Below, you\'ll find the full list of shareholders and managers with their share percentages.'
        ),
        attachTo: { element: '[data-tour="company-header"]', on: 'bottom' },
        t,
        stepIndex: 9,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="company-header"]', 5000).catch(() => {});
      },
    },

    // 10 — Timeline (chronologie)
    {
      ...createFinalStep({
        id: 'annonce-timeline',
        title: t('onboarding_tour.annonce.step10.title', 'Complete Timeline'),
        text: t(
          'onboarding_tour.annonce.step10.text',
          'This chronological timeline lists every legal publication about the company — from its constitution to the latest modification. Click any entry to read the full announcement. Perfect for tracking a company\'s evolution at a glance.'
        ),
        attachTo: { element: '[data-tour="company-timeline"]', on: 'top' },
        t,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="company-timeline"]', 5000)
          .then((el) => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return new Promise((r) => setTimeout(r, 400));
          })
          .catch(() => {});
      },
    },
  ];
}
